import type { CADMarker } from '../types/cad';

const POSITION_PRECISION = 4;

const positionKey = (marker: CADMarker): string =>
  marker.position.map((coordinate) => coordinate.toFixed(POSITION_PRECISION)).join(':');

/**
 * Group markers that resolve to the same CAD coordinate.
 *
 * DRBFM can legitimately attach many failure modes to one predefined part
 * label. Rendering every sphere and label at exactly the same coordinate makes
 * the viewer look as if only one marker exists. A grouped marker preserves all
 * detail payloads while making the shared location explicit.
 */
export const groupCADMarkersByPosition = (markers: CADMarker[]): CADMarker[] => {
  const groups = new Map<string, CADMarker[]>();

  markers.forEach((marker) => {
    const key = positionKey(marker);
    const group = groups.get(key);
    if (group) {
      group.push(marker);
    } else {
      groups.set(key, [marker]);
    }
  });

  return Array.from(groups.values()).map((group) => {
    if (group.length === 1) return group[0];

    const first = group[0];
    const detailPayload = first.detailPayload as
      | { cadAnchor?: { partLabel?: string } }
      | undefined;
    const locationLabel = detailPayload?.cadAnchor?.partLabel || first.label;
    return {
      ...first,
      label: `${locationLabel} ×${group.length}`,
      groupedMarkers: group,
    };
  });
};
